import type { GeoData } from '@/models/interfaces/geo/GeoData'
import type {
  ChartTypeResume,
  DomaineResume,
  IndicateurResume,
  ResumeCommune,
  SousDomaineResume,
} from '@/models/interfaces/ResumeStatistique'
import type { Attribut } from '@/models/interfaces/Attribut'
import type { Domaine } from '@/models/interfaces/Domaine'
import type { SousDomaine } from '@/models/interfaces/SousDomaine'
import { AttributService } from '@/services/attribut/AttributService'
import { DomaineService } from '@/services/domaine/DomaineService'
import { SousDomaineService } from '@/services/sousDomaine/SousDomaineService'
import { apiClient } from '@/plugins/axios/api'
import { libelleDynamique } from '@/utils/libelleDynamique'

const INDICATEUR_KIND = 'I'

/** Normalize API geographic scope to canonical form (cmn | uad | infrastructures | GENERAL). */
function normalizeGeographicScope(attribut: Attribut): string {
  const raw = (attribut.atgGeographicScope ?? '').toString().toLowerCase()
  if (raw === 'commune' || raw === 'cmn') return 'cmn'
  if (raw === 'uad') return 'uad'
  if (raw === 'infra' || raw === 'infrastructures') return 'infrastructures'
  return raw || 'GENERAL'
}

const CHART_LABELS_YEARS = ['2022', '2023', '2024']

const CHART_TYPES: ChartTypeResume[] = ['bar', 'line', 'doughnut']

const DUMMY_DESCRIPTIONS = [
  'Cet indicateur mesure l’évolution sur la période. La tendance est mise en perspective avec la moyenne nationale.',
  'Les données ci-dessous illustrent la situation locale et permettent une comparaison avec l’échelle nationale.',
  'L’indicateur reflète les réalisations sur le territoire. Le graphique compare la commune au référentiel national.',
]

/** Generates dummy chart data and type for an indicator (deterministic from atgId). */
function buildDummyChartData(atgId: number): {
  chartData: IndicateurResume['chartData']
  chartType: ChartTypeResume
} {
  const seed = atgId % 100
  const base = 20 + (seed % 60)
  const values = CHART_LABELS_YEARS.map((_, i) => base + i * (5 + (seed % 10)) + (seed % 5))
  const valuesNational = values.map((v) => Math.round(v * (0.85 + (seed % 15) / 100)))
  const chartType = CHART_TYPES[atgId % CHART_TYPES.length]
  return {
    chartData: {
      labels: CHART_LABELS_YEARS,
      values,
      valuesNational,
    },
    chartType,
  }
}

/** Dummy paragraph for an indicator (deterministic from atgId). */
function dummyDescription(atgId: number, atgTitre: string): string {
  return DUMMY_DESCRIPTIONS[atgId % DUMMY_DESCRIPTIONS.length].replace(
    'L’indicateur',
    `L’indicateur « ${atgTitre} »`,
  )
}

/** Dummy chiffre clé and national from last value in chart (for display). */
function dummyChiffreFromChart(chartData: NonNullable<IndicateurResume['chartData']>): {
  chiffreCle: string
  chiffreCleNational: string
} {
  const last = chartData.values[chartData.values.length - 1]
  const lastNational = chartData.valuesNational?.[chartData.values.length - 1]
  return {
    chiffreCle: String(last),
    chiffreCleNational: lastNational != null ? String(lastNational) : '',
  }
}

function buildResumeFromApi(
  communeName: string,
  domaines: Domaine[],
  sousDomaines: SousDomaine[],
  attributs: Attribut[],
): ResumeCommune {
  const pcdIndicateurs = attributs.filter(
    (a) =>
      a.atgKind === INDICATEUR_KIND &&
      a.fkSousdomaineId != null &&
      normalizeGeographicScope(a) === 'cmn',
  )

  const domainesResume: DomaineResume[] = domaines.map((domaine) => {
    const domaineSousDomaines = sousDomaines.filter((s) => s.fkDomaineId === domaine.domaineId)
    const sousDomainesResume: SousDomaineResume[] = domaineSousDomaines.map((sousDomaine) => {
      const indicateurs: IndicateurResume[] = pcdIndicateurs
        .filter((a) => a.fkSousdomaineId === sousDomaine.sousDomaineId)
        .map((a) => {
          const { chartData, chartType } = buildDummyChartData(a.atgId)
          const { chiffreCle, chiffreCleNational } = dummyChiffreFromChart(chartData!)
          return {
            libelle: libelleDynamique(a.atgTitre, a.atgTitreEn),
            chiffreCle,
            icone: a.atgIcon,
            chiffreCleNational: chiffreCleNational || undefined,
            description: a.atgDescription ?? dummyDescription(a.atgId, a.atgTitre),
            chartType,
            chartData,
          }
        })
      return {
        titre: libelleDynamique(sousDomaine.sousDomaineLib, sousDomaine.sousDomaineLibEn),
        indicateurs,
      }
    })
    return {
      titre: libelleDynamique(domaine.domaineLib, domaine.domaineLibEn),
      introDetail: undefined,
      sousDomaines: sousDomainesResume.filter((s) => s.indicateurs.length > 0),
    }
  })

  const hasData = domainesResume.some((d) => d.sousDomaines.length > 0)
  const intro = hasData
    ? `Résumé des indicateurs pour ${communeName}. Chiffres à brancher sur l’API communale.`
    : `Résumé des indicateurs pour ${communeName}. Données illustratives (API à brancher).`

  return {
    intro,
    communeName,
    domaines: domainesResume,
  }
}

function getMockResume(communeName: string): ResumeCommune {
  return {
    intro: `Résumé des indicateurs pour ${communeName}. Comparaison avec l'échelle nationale. Données illustratives (API à brancher).`,
    communeName,
    domaines: [
      {
        titre: 'Domaine 1',
        introDetail: 'Introduction du premier domaine.',
        sousDomaines: [
          {
            titre: 'Sous-domaine 1.1',
            indicateurs: [
              {
                libelle: 'Indicateur A',
                chiffreCle: '42',
                chiffreCleNational: '38',
                description:
                  'L’indicateur « Indicateur A » mesure l’évolution sur la période. La tendance est mise en perspective avec la moyenne nationale.',
                chartType: 'bar',
                chartData: {
                  labels: ['2022', '2023', '2024'],
                  values: [35, 40, 42],
                  valuesNational: [32, 36, 38],
                },
              },
            ],
          },
          {
            titre: 'Sous-domaine 1.2',
            indicateurs: [
              {
                libelle: 'Indicateur B',
                chiffreCle: '78 %',
                chiffreCleNational: '72 %',
                description:
                  'Les données ci-dessous illustrent la situation locale pour l’indicateur B et permettent une comparaison avec l’échelle nationale.',
                chartType: 'line',
                chartData: {
                  labels: ['Jan', 'Fév', 'Mar'],
                  values: [70, 75, 78],
                  valuesNational: [68, 70, 72],
                },
              },
            ],
          },
        ],
      },
      {
        titre: 'Domaine 2',
        introDetail: 'Introduction du second domaine.',
        sousDomaines: [
          {
            titre: 'Sous-domaine 2.1',
            indicateurs: [
              {
                libelle: 'Indicateur C',
                chiffreCle: '120',
                chiffreCleNational: '115',
                description:
                  'L’indicateur « Indicateur C » reflète les réalisations sur le territoire. Le graphique compare la commune au référentiel national.',
                chartType: 'doughnut',
                chartData: {
                  labels: ['Q1', 'Q2', 'Q3', 'Q4'],
                  values: [90, 100, 110, 120],
                  valuesNational: [95, 102, 108, 115],
                },
              },
            ],
          },
        ],
      },
    ],
  }
}

/**
 * GET /api/communes/{cmnCode}/resume_statistique — not implemented backend-side yet
 * (see ISSUE_API_RESUME_STATISTIQUE_ENDPOINT.md). Returns null on any failure (404 while
 * pending, network error, empty payload) so the caller falls back to illustrative data.
 */
async function fetchResumeCommuneFromApi(cmnCode: string): Promise<ResumeCommune | null> {
  try {
    const response = await apiClient.get<ResumeCommune>(
      `/api/communes/${encodeURIComponent(cmnCode)}/resume_statistique`,
    )
    const data = response.data
    if (data && Array.isArray(data.domaines) && data.domaines.length > 0) {
      return data
    }
    return null
  } catch {
    return null
  }
}

/**
 * Loads communal statistical summary: domaines, sous-domaines, indicateurs (the 53 of the PCD
 * classification, direct attribut -> sousdomaine link). Tries the real per-commune endpoint first
 * (cmnCode), then real domaines/sousDomaines/indicateurs with illustrative chiffres, then a fully
 * mocked fallback if the referential APIs are also unavailable.
 */
export async function getResumeCommune(
  commune: GeoData | null,
  cmnCode?: string | null,
): Promise<ResumeCommune> {
  const communeName = commune?.name ?? 'SOA'

  if (cmnCode) {
    const real = await fetchResumeCommuneFromApi(cmnCode)
    if (real) return real
  }

  try {
    const [domaines, sousDomaines, indicateurs] = await Promise.all([
      DomaineService.getAll(),
      SousDomaineService.getAll(),
      AttributService.getIndicateursPcd(),
    ])

    if (domaines.length > 0) {
      return buildResumeFromApi(communeName, domaines, sousDomaines, indicateurs)
    }
  } catch {
    console.debug('ResumeStatistique: APIs unavailable, using mock data.')
  }

  return getMockResume(communeName)
}
